import type { ChunkRangeOut } from "daedalus-core";
import type { ChunkRange } from "daedalus-renderer-phaser";

/**
 * When the loading shell may reveal the world (P1.4-T5): on a frame the renderer has drawn, never at the
 * welcome. The frame must come after the welcome and the first snapshot (so the things and characters in
 * view are drawn too), show the local character, and show every chunk its camera saw painted. The camera's
 * view rectangle is the one the frame was drawn with: Phaser sets `worldView` as it renders, after the scene's
 * update moved the camera. The chunk painter works from the scroll that update set (`camera-view.ts`), which gives
 * the same rectangle, so the chunks a frame shows are the ones the painter finished in that frame's update.
 */

/**
 * A camera's view in world pixels: the rectangle Phaser's `worldView` holds.
 */
export interface ViewRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * What one drawn frame showed: whether the welcome and the first snapshot had arrived before it, whether it
 * drew the local character, the chunk range painted, and the chunks its camera showed.
 */
export interface RevealFrame {
  readonly welcomed: boolean;
  readonly snapshot: boolean;
  readonly character: boolean;
  readonly painted: ChunkRange | null;
  readonly needed: ChunkRange | null;
}

/**
 * The chunks a view rectangle shows, with no margin: every chunk the part of the view inside the map
 * touches. An edge on a chunk boundary does not reach into the next chunk.
 *
 * @param view The view in world pixels, as the frame was drawn.
 * @param tileSize Tile size in pixels.
 * @param chunkSize Chunk size in tiles.
 * @param columns Chunk columns in the map.
 * @param rows Chunk rows in the map.
 * @param out A range to fill and return in place of a new one, for a caller asking every frame.
 * @returns The inclusive range, or null when the view shows none of the map.
 * @example
 * chunksCovering({ left: 3700, top: 3900, width: 960, height: 640 }, 32, 32, 8, 8);
 * // { minCx: 3, minCy: 3, maxCx: 4, maxCy: 4 }
 */
export function chunksCovering(view: ViewRect, tileSize: number, chunkSize: number, columns: number, rows: number, out?: ChunkRangeOut): ChunkRange | null {
  const pixels = tileSize * chunkSize;
  const left = Math.max(0, view.left);
  const top = Math.max(0, view.top);
  const right = Math.min(columns * pixels, view.left + view.width);
  const bottom = Math.min(rows * pixels, view.top + view.height);
  if (right <= left || bottom <= top) {
    return null;
  }
  const range = out ?? { minCx: 0, minCy: 0, maxCx: 0, maxCy: 0 };
  range.minCx = Math.floor(left / pixels);
  range.minCy = Math.floor(top / pixels);
  range.maxCx = Math.ceil(right / pixels) - 1;
  range.maxCy = Math.ceil(bottom / pixels) - 1;
  return range;
}

/**
 * Whether a painted range holds every chunk a view needs.
 *
 * @param painted The chunks painted, or null when none are.
 * @param needed The chunks the view shows, or null when it shows none.
 * @returns True when every needed chunk is painted.
 */
export function covers(painted: ChunkRange | null, needed: ChunkRange | null): boolean {
  if (needed === null) {
    return true;
  }
  if (painted === null) {
    return false;
  }
  return painted.minCx <= needed.minCx && painted.minCy <= needed.minCy && painted.maxCx >= needed.maxCx && painted.maxCy >= needed.maxCy;
}

/**
 * Whether a drawn frame may reveal the world.
 *
 * @param frame What the frame showed.
 * @returns True when the welcome and the first snapshot came before it and it drew the local character over
 *   every chunk its camera showed.
 */
export function readyToReveal(frame: RevealFrame): boolean {
  return frame.welcomed && frame.snapshot && frame.character && covers(frame.painted, frame.needed);
}
